// Document Center — history at scale (00-ds-09, D148 §9). Deterministic mock
// rows so the Documents › Filed, Virtual cards › Charged and Checks history can
// actually page, search and filter in the browser over hundreds/thousands of
// rows. Real data comes from the server index (§8); nothing here is a network
// call. Seeded, so every reload shows the same set.

import {
  MOCK_NOW,
  generate,
  pick,
  int,
  money,
  fullName,
  pastDate,
  PAYERS,
} from '@/components/lists';
import type { Card, Check, Doc, DocKind } from './types';
import { PMS } from '@/features/shared/pms';

// ── sizes (§9 "after 2 years" volumes for 1–5 locations) ──
export const DC_FILED_HISTORY_COUNT = 1500; // Documents › Filed (4k–36k; 1.5k here)
export const DC_CHARGED_HISTORY_COUNT = 400; // Virtual cards › Charged (120–1,500)
export const DC_CHECK_HISTORY_COUNT = 500; // Checks (500–7k)

// ── the "type" filter choices for Documents › Filed (§3: Documents adds "type") ──
export const DC_DOC_TYPE_FILTER: { value: DocKind; label: string }[] = [
  { value: 'eob_check', label: 'EOB + check' },
  { value: 'eob', label: 'EOB' },
  { value: 'request', label: 'Request letter' },
  { value: 'denial', label: 'Denial letter' },
  { value: 'preauth', label: 'Pre-auth answer' },
  { value: 'insurance_card', label: 'Insurance card' },
  { value: 'virtual_card', label: 'Virtual card' },
  { value: 'other', label: 'Referral / other' },
];

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** "Friday, October 10, 2026" — the day-group header for Documents › Filed. */
export function dayGroupLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return `${WEEKDAYS[d.getDay()] ?? ''}, ${MONTHS_LONG[d.getMonth()] ?? ''} ${String(d.getDate())}, ${String(d.getFullYear())}`;
}
/** "Oct 10" — the short row date. */
export function shortDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  const m = (MONTHS_LONG[d.getMonth()] ?? '').slice(0, 3);
  return `${m} ${String(d.getDate())}`;
}
function usLong(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return `${(MONTHS_LONG[d.getMonth()] ?? '').slice(0, 3)} ${String(d.getDate())}, ${String(d.getFullYear())}`;
}

const CLAIM_PREFIX = ['AET', 'DD', 'CIG', 'MET', 'UHC', 'GRD', 'SUN', 'BCBS', 'HUM'] as const;
function checkNumber(r: () => number): string {
  return String(int(r, 1_000_000, 99_999_999));
}
function traceNumber(r: () => number): string {
  return String(int(r, 100_000_000_000, 999_999_999_999));
}
function claimNumber(r: () => number): string {
  return `${pick(r, CLAIM_PREFIX)}-${String(int(r, 2024, 2026))}-${String(int(r, 100_000, 999_999))}`;
}
function referenceNumber(r: () => number): string {
  return `${pick(r, ['PA', 'REF', 'RFI', 'AUTH'])}-${String(int(r, 10_000, 99_999))}`;
}

// ── Documents › Filed (history) ───────────────────────────────────────────
const SOURCES = ['Fax', 'Portal', 'iPad photo', 'Mail scan'] as const;

export function buildFiledHistory(): Doc[] {
  return generate<Doc>(DC_FILED_HISTORY_COUNT, 9001, (r, i) => {
    const t = pick(r, DC_DOC_TYPE_FILTER);
    const kind = t.value;
    const when = pastDate(r, 18);
    const patient = fullName(r);
    const payer = pick(r, PAYERS);
    const amount = money(r, 68, 5200);
    const chk = checkNumber(r);
    const clm = claimNumber(r);
    const ref = referenceNumber(r);
    const source = pick(r, SOURCES);
    const pageCount = int(r, 1, 4);
    const metaLine = `${source} from ${payer} · ${usLong(when.iso)} · ${String(pageCount)} page${pageCount === 1 ? '' : 's'}`;

    let title: string;
    let wentToLabel: string;
    let status: Doc['status'] = 'done';
    let doneTitle: string;
    let doneSteps: Doc['doneSteps'] = [];
    let doneLinks: Doc['doneLinks'] = [];
    const read: Doc['read'] = [
      { label: 'Payer', value: payer },
      { label: 'Patient', value: patient },
    ];
    let filedTicks: string[] | undefined;

    switch (kind) {
      case 'eob_check':
        title = `${payer} · check ${chk} · ${money2(amount)}`;
        wentToLabel = 'Posting';
        doneTitle = 'ELVA sent it to Posting';
        doneSteps = [{ text: `read the EOB and sent ${String(int(r, 1, 9))} claims to Posting, ${money2(amount)}.` }];
        doneLinks = [{ label: 'See the check in Document Center › Checks', href: '/document-center/checks' }];
        read.push({ label: 'Check', value: `${chk} · ${money2(amount)}` });
        break;
      case 'eob':
        title = `${payer} EOB for ${patient} · ${money2(amount)}`;
        wentToLabel = 'Posting';
        doneTitle = 'ELVA posted the EOB';
        doneSteps = [{ text: `matched it to ${patient}'s claim and posted ${money2(amount)}.` }];
        doneLinks = [{ label: 'See the posting in Posting', href: '/posting' }];
        read.push({ label: 'Claim', value: clm });
        break;
      case 'request':
        title = `${payer} asks for records for ${patient}`;
        wentToLabel = 'ELVA sent it · Insurance A/R';
        doneTitle = 'ELVA answered the request';
        doneSteps = [{ text: `uploaded the records to ${payer}'s portal for claim ${clm}.` }];
        read.push({ label: 'Claim', value: clm }, { label: 'Reference', value: ref });
        break;
      case 'denial':
        title = `${payer} denied ${patient}'s claim`;
        wentToLabel = 'Denied & underpaid';
        doneTitle = 'ELVA opened a denial';
        doneSteps = [{ text: `read the denial and started the appeal for claim ${clm}.` }];
        doneLinks = [{ label: 'See it in Denied & underpaid', href: '/denied' }];
        read.push({ label: 'Claim', value: clm });
        break;
      case 'preauth':
        title = `${payer} ${pick(r, ['approved', 'denied'])} ${patient}'s pre-auth`;
        wentToLabel = 'Pre-auths';
        doneTitle = 'ELVA updated the pre-auth';
        doneSteps = [{ text: `marked the pre-auth, ref ${ref}.` }];
        doneLinks = [{ label: 'See it in Pre-auths', href: '/pre-auths' }];
        read.push({ label: 'Reference', value: ref });
        break;
      case 'insurance_card':
        title = `${patient} · insurance card`;
        wentToLabel = 'Patient insurance · plan checked';
        doneTitle = 'ELVA saved the card and checked the plan';
        doneSteps = [{ text: `saved the card to ${patient}'s insurance and checked eligibility with Stedi.` }];
        read.push({ label: 'Member ID', value: `W${String(int(r, 100_000_000, 999_999_999))}` });
        break;
      case 'virtual_card':
        title = `${payer} · virtual card · ${money2(amount)}`;
        wentToLabel = 'Virtual cards';
        doneTitle = 'ELVA posted the card payment';
        doneSteps = [{ text: `posted ${money2(amount)} after the card was charged.` }];
        read.push({ label: 'Amount', value: money2(amount) });
        break;
      default:
        title = `${pick(r, ['Referral letter', 'Records request', 'Statement', 'Other paper'])} for ${patient}`;
        wentToLabel = 'Filed, no work needed';
        status = 'filed';
        doneTitle = 'Filed, no work needed';
        filedTicks = [
          'Not an insurance document, so there’s no RCM work.',
          `Filed to ${patient}’s documents in ${PMS}.`,
        ];
    }

    const doc: Doc = {
      id: `DH-${String(i)}`,
      kind,
      kindLabel: t.label,
      group: 'filed',
      status,
      source: `${source} · ${usLong(when.iso)}`,
      metaLine,
      title,
      wentToLabel,
      pages: [],
      read,
      doneTone: 'done',
      doneTitle,
      doneSteps,
      doneLinks,
      history: `History: received ${usLong(when.iso)} · read by ELVA the same day`,
      receivedIso: when.iso,
      patient,
      payerName: payer,
      checkNo: kind === 'eob_check' ? chk : '',
      claimNo: clm,
      reference: ref,
    };
    if (filedTicks) doc.filedTicks = filedTicks;
    return doc;
  });
}

// ── Virtual cards › Charged (history) ──────────────────────────────────────
export function buildChargedHistory(): Card[] {
  return generate<Card>(DC_CHARGED_HISTORY_COUNT, 9002, (r, i) => {
    const payer = pick(r, PAYERS);
    const when = pastDate(r, 18);
    const nClaims = int(r, 1, 3);
    const claims = Array.from({ length: nClaims }, () => ({
      patient: fullName(r),
      visit: `visit ${usLong(pastDate(r, 19).iso)}`,
      amount: money(r, 40, 900),
    }));
    const amount = Math.round(claims.reduce((s, c) => s + c.amount, 0) * 100) / 100;
    r(); // keeps the seeded sequence stable
    const card: Card = {
      id: `CH-${String(i)}`,
      payer,
      amount,
      last4: String(int(r, 1000, 9999)),
      expires: `${String(int(r, 1, 12)).padStart(2, '0')}/${String(int(r, 26, 28))}`,
      receivedDisplay: `Received ${usLong(pastDate(r, 19).iso)}`,
      expiresDisplay: 'charged',
      source: { kind: 'fax', time: '' },
      status: 'charged',
      claims,
      chargedBy: 'Charged by Ava Thompson (Lone Star Billing)',
      chargedApproval: String(int(r, 100_000, 999_999)),
      chargedDate: usLong(when.iso),
      chargedIso: when.iso,
    };
    // Mostly fax; every fifth is a portal payment. No random draws, so the seeded sequence stays stable.
    card.source = i % 5 === 3
      ? { kind: 'portal', ref: `payment ${String(1000 + ((i * 37) % 9000))}-${String(1000 + ((i * 91) % 9000))}` }
      : { kind: 'fax', time: `${card.receivedDisplay.replace('Received ', '')}, ${String(8 + (i % 9))}:${String((i * 7) % 6)}${String((i * 3) % 10)} AM` };
    return card;
  });
}

// ── Checks (history) ───────────────────────────────────────────────────────
export function buildCheckHistory(): Check[] {
  // Every check received is listed (D124). The Bluebonnet EOB (check 30071) is
  // still being read by a person, but the check itself is already received, so
  // it must appear in Checks today, not in the bank yet.
  const todayIso = MOCK_NOW.toISOString().slice(0, 10);
  const bluebonnet: Check = {
    id: 'KH-BB-30071',
    receivedDisplay: usLong(todayIso),
    payer: 'Bluebonnet Dental Plan',
    checkNo: '30071',
    amount: 412.8,
    postedLabel: 'Being read · a person needs to read the EOB',
    inBankLabel: 'Not yet',
    inBankTone: 'none',
    receivedIso: todayIso,
    traceNo: traceNumber(() => 0.5),
    patient: 'Adam Ross',
  };
  const rest = generate<Check>(DC_CHECK_HISTORY_COUNT, 9003, (r, i) => {
    const payer = pick(r, PAYERS);
    const when = pastDate(r, 18);
    const amount = money(r, 90, 6400);
    // Most are matched to a deposit; a few recent ones are not in the bank yet.
    const daysAgo = Math.round((MOCK_NOW.getTime() - new Date(`${when.iso}T12:00:00`).getTime()) / 86_400_000);
    const notYet = daysAgo <= 3;
    return {
      id: `KH-${String(i)}`,
      receivedDisplay: usLong(when.iso),
      payer,
      checkNo: checkNumber(r),
      amount,
      postedLabel: `Posted ${shortDate(when.iso)}`,
      inBankLabel: notYet ? 'Not yet' : `Deposited ${shortDate(when.iso)}`,
      inBankTone: notYet ? 'none' : 'ok',
      receivedIso: when.iso,
      traceNo: traceNumber(r),
      patient: fullName(r),
    };
  });
  return [bluebonnet, ...rest];
}

/** Checks that two later statements never showed — pinned across every month (§9). */
export function buildMissingHistory(): Check[] {
  return generate<Check>(4, 9004, (r, i) => {
    const payer = pick(r, PAYERS);
    const when = pastDate(r, 10);
    return {
      id: `KM-${String(i)}`,
      receivedDisplay: usLong(when.iso),
      payer,
      checkNo: checkNumber(r),
      amount: money(r, 300, 2400),
      postedLabel: `Posted ${shortDate(when.iso)}`,
      inBankLabel: 'In Human in the loop: do you have the check?',
      inBankTone: 'warn',
      missingHitl: true,
      receivedIso: when.iso,
      traceNo: traceNumber(r),
      patient: fullName(r),
    };
  });
}

function money2(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
